import { Link } from 'react-router-dom';
import { Eye, ArrowLeft, CheckCircle } from 'lucide-react';

export default function Accessibility() {
  return (
    <div className="max-w-4xl mx-auto">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Home
      </Link>

      <div className="flex items-center gap-3 mb-8">
        <Eye className="w-8 h-8 text-accent-blue" />
        <h1 className="text-3xl font-bold text-white">Accessibility Statement</h1>
      </div>

      <div className="prose prose-invert max-w-none space-y-8">
        <p className="text-gray-400">
          <strong>Last Updated:</strong> January 26, 2025
        </p>

        <section>
          <h2 className="text-xl font-semibold text-white">Our Commitment</h2>
          <p className="text-gray-300">
            Northcoast AI Academy is committed to ensuring digital accessibility for people of all abilities.
            We continually improve the user experience for everyone and apply relevant accessibility standards
            to ensure we provide equal access to all users.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">Conformance Status</h2>
          <p className="text-gray-300">
            We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards.
            These guidelines explain how to make web content more accessible for people with disabilities.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">Accessibility Features</h2>
          <p className="text-gray-300">
            Our platform includes the following accessibility features:
          </p>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="bg-gray-800/50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Visual
              </h3>
              <ul className="text-gray-300 text-sm mt-2 space-y-1">
                <li>Adjustable font sizes (small, medium, large, extra-large)</li>
                <li>High contrast mode</li>
                <li>Dark theme optimized for readability</li>
                <li>Text alternatives for images</li>
                <li>Color choices that accommodate color blindness</li>
              </ul>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Motor
              </h3>
              <ul className="text-gray-300 text-sm mt-2 space-y-1">
                <li>Full keyboard navigation</li>
                <li>Skip navigation links</li>
                <li>Large click targets</li>
                <li>No time limits on interactions</li>
                <li>Reduced motion option</li>
              </ul>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Cognitive
              </h3>
              <ul className="text-gray-300 text-sm mt-2 space-y-1">
                <li>Clear, consistent navigation</li>
                <li>Plain language content</li>
                <li>Progress indicators</li>
                <li>Break reminders during lessons</li>
                <li>Dyslexia-friendly font option</li>
              </ul>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Auditory
              </h3>
              <ul className="text-gray-300 text-sm mt-2 space-y-1">
                <li>Text-based content primary</li>
                <li>Captions for video content</li>
                <li>Visual indicators for audio cues</li>
                <li>Transcripts available</li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">Customization Options</h2>
          <p className="text-gray-300">
            You can customize your experience in Account Settings &gt; Accessibility Preferences:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li><strong>Font Size:</strong> Small, Medium, Large, Extra Large</li>
            <li><strong>Font Family:</strong> Default, Dyslexia-Friendly (OpenDyslexic), Monospace</li>
            <li><strong>High Contrast:</strong> Enhanced color contrast for better visibility</li>
            <li><strong>Reduced Motion:</strong> Minimize animations and transitions</li>
            <li><strong>Text-to-Speech:</strong> Enable screen reader optimizations</li>
            <li><strong>Break Reminders:</strong> Periodic prompts to take breaks during learning</li>
            <li><strong>Playback Speed:</strong> Adjust audio/video playback rate</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">Assistive Technology Support</h2>
          <p className="text-gray-300">
            Our platform is designed to work with:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li>Screen readers (NVDA, JAWS, VoiceOver, TalkBack)</li>
            <li>Screen magnification software</li>
            <li>Speech recognition software</li>
            <li>Alternative keyboard and pointing devices</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">Keyboard Shortcuts</h2>
          <div className="bg-gray-800/50 rounded-lg p-4 mt-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400">
                  <th className="pb-2">Shortcut</th>
                  <th className="pb-2">Action</th>
                </tr>
              </thead>
              <tbody className="text-gray-300">
                <tr>
                  <td className="py-1"><kbd className="bg-gray-700 px-2 py-0.5 rounded">Tab</kbd></td>
                  <td>Navigate between interactive elements</td>
                </tr>
                <tr>
                  <td className="py-1"><kbd className="bg-gray-700 px-2 py-0.5 rounded">Enter</kbd></td>
                  <td>Activate focused element</td>
                </tr>
                <tr>
                  <td className="py-1"><kbd className="bg-gray-700 px-2 py-0.5 rounded">Esc</kbd></td>
                  <td>Close modals and menus</td>
                </tr>
                <tr>
                  <td className="py-1"><kbd className="bg-gray-700 px-2 py-0.5 rounded">?</kbd></td>
                  <td>Show keyboard shortcuts help</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">Neurodiversity Support</h2>
          <p className="text-gray-300">
            As part of our commitment to inclusive education, we specifically support neurodivergent learners:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li>Our "Neurodiverse Superpowers" track provides AI scaffolding strategies for ADHD, autism, and other cognitive differences</li>
            <li>Lessons can be completed at your own pace without time pressure</li>
            <li>Break reminders help maintain focus and prevent burnout</li>
            <li>Clear structure and predictable navigation reduce cognitive load</li>
            <li>AI coach can adapt explanations to different learning styles</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">Known Limitations</h2>
          <p className="text-gray-300">
            We're actively working to address these known issues:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li>Some older PDF resources may not be fully accessible</li>
            <li>Third-party embedded content may have varying accessibility</li>
            <li>Live AI chat interactions may have delayed screen reader announcements</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">Feedback and Support</h2>
          <p className="text-gray-300">
            We welcome your feedback on the accessibility of Northcoast AI Academy. If you encounter accessibility barriers or have suggestions for improvement:
          </p>
          <p className="text-gray-300 mt-2">
            <strong>Email:</strong> accessibility@northcoast.ai<br />
            <strong>Response Time:</strong> We aim to respond within 2 business days<br />
          </p>
          <p className="text-gray-300 mt-2">
            When contacting us, please include:
          </p>
          <ul className="list-disc list-inside text-gray-300 mt-2 space-y-1">
            <li>Description of the issue</li>
            <li>The page URL where you encountered the problem</li>
            <li>Your browser and assistive technology (if applicable)</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">Ongoing Efforts</h2>
          <p className="text-gray-300">
            We are committed to continuous improvement:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li>Regular accessibility audits</li>
            <li>User testing with people who use assistive technologies</li>
            <li>Training our team on accessibility best practices</li>
            <li>Monitoring and fixing issues as they're discovered</li>
            <li>Incorporating accessibility into new feature development</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">Legal</h2>
          <p className="text-gray-300">
            This statement was last updated on January 26, 2025. We review and update this statement regularly as we continue to improve accessibility across our platform.
          </p>
        </section>
      </div>
    </div>
  );
}
